import { clients } from '@/lib/https';
import { getIamServiceUrl } from '@/lib/api/iam-service-url';
import type { ApiKey } from './api-keys.types';

const API_KEYS_PATH = '/iam/v4/auth/client-credentials';

const stringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

const maskClientSecret = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length === 0) return null;
  // Never reveal all characters of a short value through overlapping ends.
  return value.length > 8 ? `${value.slice(0, 4)}...${value.slice(-4)}` : '...';
};

export async function getApiKeys(organizationId: string): Promise<ApiKey[]> {
  if (!organizationId) throw new Error('Select an organization to view API keys.');
  const response = await clients.get<unknown>(getIamServiceUrl(API_KEYS_PATH));
  if (!Array.isArray(response)) throw new Error('Invalid API keys response');

  return response.flatMap((value: unknown): ApiKey[] => {
    if (!value || typeof value !== 'object') throw new Error('Invalid API keys response');
    const record = value as Record<string, unknown>;
    if (typeof record.organizationId !== 'string') throw new Error('Invalid API keys response');
    if (record.organizationId !== organizationId) return [];
    if (
      typeof record.itemId !== 'string' ||
      typeof record.name !== 'string' ||
      typeof record.isActive !== 'boolean' ||
      typeof record.createdDate !== 'string' ||
      !stringArray(record.permissions) ||
      !stringArray(record.roles)
    ) {
      throw new Error('Invalid API keys response');
    }

    // Construct an allowlisted object before React Query caches anything. Never spread the
    // IAM record: this endpoint includes clientSecret, unlike the Campaign read contract.
    return [
      {
        itemId: record.itemId,
        organizationId: record.organizationId,
        name: record.name,
        accessTokenValidForNumberMinutes:
          typeof record.accessTokenValidForNumberMinutes === 'number'
            ? record.accessTokenValidForNumberMinutes
            : undefined,
        scopes: [...record.permissions],
        roles: [...record.roles],
        status: record.isActive ? 'active' : 'inactive',
        createdDate: record.createdDate,
        clientId: record.itemId,
        clientSecretPreview: maskClientSecret(record.clientSecret),
        // Token TTL is not the key's expiry; IAM does not supply these dates.
        expiresAt: null,
        lastUsedAt: null,
      },
    ];
  });
}

export async function revokeApiKey(itemId: string): Promise<void> {
  if (!itemId.trim()) throw new Error('Select a key to revoke.');
  const response = await clients.delete<unknown>(
    getIamServiceUrl(`${API_KEYS_PATH}/${encodeURIComponent(itemId)}`)
  );
  // DELETE may return an empty response or the IAM success envelope.
  if (response === null) return;
  const result =
    response && typeof response === 'object' ? (response as Record<string, unknown>) : null;
  const errors = result?.errors;
  const hasErrors = Array.isArray(errors) ? errors.length > 0 : !!errors;
  if (result?.isSuccess !== true || hasErrors) {
    throw new Error('Could not revoke the API key. Please try again.');
  }
}

export async function createApiKey(input: {
  itemId?: string;
  roles?: string[];
  accessTokenValidForNumberMinutes?: number;
  name: string;
  permissions: string[];
  organizationId: string;
}): Promise<void> {
  const name = input.name.trim();
  if (!name) throw new Error('Enter a name for this key.');
  if (!input.organizationId) throw new Error('Select an organization before issuing a key.');
  if (!input.permissions.length && !input.roles?.length)
    throw new Error('Select at least one scope.');
  const projectKey = import.meta.env.VITE_X_BLOCKS_KEY;
  if (!projectKey) throw new Error('VITE_X_BLOCKS_KEY is not configured');

  const response = await clients.post<unknown>(
    getIamServiceUrl(API_KEYS_PATH),
    JSON.stringify({
      itemId: input.itemId?.trim() || null,
      name,
      isActive: true,
      accessTokenValidForNumberMinutes: input.accessTokenValidForNumberMinutes ?? 15,
      roles: input.roles ?? [],
      permissions: [...new Set(input.permissions)],
      projectKey,
      organizationId: input.organizationId,
    })
  );
  const result =
    response && typeof response === 'object' ? (response as Record<string, unknown>) : null;
  const errors = result?.errors;
  const hasErrors = Array.isArray(errors) ? errors.length > 0 : !!errors;
  if (result?.isSuccess !== true || hasErrors) {
    const messages = Array.isArray(errors)
      ? errors
          .map((error: unknown) => {
            if (typeof error === 'string') return error;
            if (
              error &&
              typeof error === 'object' &&
              'message' in error &&
              typeof error.message === 'string'
            )
              return error.message;
            return '';
          })
          .filter(Boolean)
      : errors && typeof errors === 'object'
        ? Object.values(errors).filter((message): message is string => typeof message === 'string')
        : typeof errors === 'string'
          ? [errors]
          : [];
    throw new Error(messages.join(' ') || 'Could not issue the API key. Please try again.');
  }
}
